import { createContext, FC, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useShowContext } from 'framework/show';
import { EngineeringBugReport, ProjectEngineeringGraphqlData } from '../types';
import CreateBugModal from './CreateBugModal';
import UpdateBugModal from './UpdateBugModal';

interface BugCrudContextValue {
  openCreate: () => void;
  openUpdate: (bug: EngineeringBugReport) => void;
}

const BugCrudContext = createContext<BugCrudContextValue | null>(null);

interface BugCrudProviderProps {
  projectId: string;
  children: ReactNode;
}

export const BugCrudProvider: FC<BugCrudProviderProps> = ({ projectId, children }) => {
  const { data: showData, setData } = useShowContext<ProjectEngineeringGraphqlData>();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingBug, setEditingBug] = useState<EngineeringBugReport | null>(null);

  const value = useMemo<BugCrudContextValue>(
    () => ({
      openCreate: () => setIsCreateOpen(true),
      openUpdate: bug => setEditingBug(bug),
    }),
    [],
  );

  const closeCreate = useCallback(() => setIsCreateOpen(false), []);
  const closeUpdate = useCallback(() => setEditingBug(null), []);

  const upsertBug = useCallback(
    (bug: EngineeringBugReport) => {
      const current = showData.getData();
      const bugs = current?.engineeringBugs ?? [];
      const nextBugs = bugs.some(item => item.id === bug.id)
        ? bugs.map(item => (item.id === bug.id ? bug : item))
        : [bug, ...bugs];
      setData?.({ ...current, engineeringBugs: nextBugs });
    },
    [showData, setData],
  );

  return (
    <BugCrudContext.Provider value={value}>
      {children}
      <CreateBugModal projectId={projectId} isOpen={isCreateOpen} onClose={closeCreate} onSaved={upsertBug} />
      <UpdateBugModal
        projectId={projectId}
        bug={editingBug}
        isOpen={!!editingBug}
        onClose={closeUpdate}
        onSaved={upsertBug}
      />
    </BugCrudContext.Provider>
  );
};

export const useBugCrud = (): BugCrudContextValue => {
  const ctx = useContext(BugCrudContext);
  if (!ctx) {
    throw new Error('useBugCrud must be used within a BugCrudProvider');
  }
  return ctx;
};
